import { Capacitor } from '@capacitor/core';
import { PushNotifications, type Token } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import type {
  DeviceAudience,
  DevicePlatform,
  RegisterDeviceRequest,
  RegisterDeviceResponse,
} from './types';

const INSTALLATION_ID_KEY = 'pp.push.installationId';

/**
 * Per-app configuration. Each Capacitor bundle is one audience (its own
 * Notification Hub installation), so audience is fixed at app-init time.
 * The register/unregister callbacks delegate the HTTP work to the host
 * app's apiClient — the shared push module stays HTTP-agnostic.
 */
export interface PushConfig {
  audience: DeviceAudience;
  registerDevice: (body: RegisterDeviceRequest) => Promise<RegisterDeviceResponse>;
  unregisterDevice: (installationId: string) => Promise<void>;
}

let pushConfig: PushConfig | null = null;
let listenersWired = false;

/**
 * Wire push to the host app's HTTP layer + audience. Must be called before
 * any other push helper. Throws on subsequent helpers if omitted, since
 * the helpers have no sane default backend to talk to.
 */
export function configurePush(config: PushConfig): void {
  pushConfig = config;
}

function requireConfig(): PushConfig {
  if (!pushConfig) {
    throw new Error(
      'configurePush() must be called before using @pp/shared/push helpers.',
    );
  }
  return pushConfig;
}

function platformCode(): DevicePlatform | null {
  switch (Capacitor.getPlatform()) {
    case 'ios':
      return 0;
    case 'android':
      return 1;
    default:
      // Web push isn't routed through Azure Notification Hubs; the
      // backend will reject Platform=2 (Web) with NotSupported.
      return null;
  }
}

async function readStoredInstallationId(): Promise<string | undefined> {
  const { value } = await Preferences.get({ key: INSTALLATION_ID_KEY });
  return value ?? undefined;
}

async function storeInstallationId(id: string): Promise<void> {
  await Preferences.set({ key: INSTALLATION_ID_KEY, value: id });
}

async function clearInstallationId(): Promise<void> {
  await Preferences.remove({ key: INSTALLATION_ID_KEY });
}

/**
 * Whether push registration is even possible in the current runtime.
 * Returns false on web (use it to hide the in-app "enable notifications"
 * affordance there) and on unsupported native shells.
 */
export function pushSupported(): boolean {
  return platformCode() !== null;
}

/**
 * Returns true if the user has already granted push permission. iOS only
 * lets you ask once, so checking before prompting is the right pattern.
 */
export async function pushPermissionGranted(): Promise<boolean> {
  if (!pushSupported()) return false;
  const status = await PushNotifications.checkPermissions();
  return status.receive === 'granted';
}

/**
 * Returns the InstallationId we hold for this device, if any. Useful for
 * distinguishing "OS permission granted AND we successfully registered
 * with the backend" from "OS permission granted but the backend never
 * heard about us" — the latter happens when a register POST failed during
 * a prior session and was silently swallowed. Without checking this, the
 * Settings toggle can lie about state.
 */
export async function getStoredInstallationId(): Promise<string | undefined> {
  if (!pushSupported()) return undefined;
  return readStoredInstallationId();
}

/**
 * Request permission + register with the OS push service. Resolves once
 * the OS has issued a token AND we've sent it to the backend. Returns
 * the InstallationId we now hold on the device, or null if the user
 * denied or we're on web.
 */
export async function enablePush(deviceName?: string): Promise<string | null> {
  if (!pushSupported()) {
    console.log('[push] enablePush: not supported on this platform');
    return null;
  }

  const platform = platformCode();
  if (platform === null) {
    console.log('[push] enablePush: no platform code');
    return null;
  }

  const { audience, registerDevice } = requireConfig();

  console.log('[push] enablePush: requesting permissions');
  const perm = await PushNotifications.requestPermissions();
  console.log('[push] enablePush: permission result =', perm.receive);
  if (perm.receive !== 'granted') return null;

  // The plugin fires the registration event asynchronously after
  // PushNotifications.register() — we have to bridge that into a
  // promise so the caller can await the registration result.
  wirePushEventListeners();

  // Wire BOTH listeners and capture their handles BEFORE calling
  // register(). Previously these were set up via `.then((h) => ...)`
  // which created a race: when iOS has a cached APNs token (typical
  // after the first ever grant), the 'registration' event fires
  // synchronously from inside register(), before the .then promise
  // resolves. The handler ran without the handle being stored, so
  // cleanup couldn't remove the listener — a leak that turns into
  // duplicate handlers on the next enablePush call. Awaiting the
  // addListener Promises first ensures the handles exist before any
  // event can fire.
  let resolveResult!: (value: string | null) => void;
  let rejectResult!: (reason: unknown) => void;
  const resultPromise = new Promise<string | null>((resolve, reject) => {
    resolveResult = resolve;
    rejectResult = reject;
  });

  const tokenHandle = await PushNotifications.addListener(
    'registration',
    async (token: Token) => {
      console.log(
        '[push] registration event fired, token length =',
        token.value?.length ?? 0,
      );
      try {
        const stored = await readStoredInstallationId();
        console.log(
          '[push] calling /api/devices/register, stored id =',
          stored ?? '(none)',
        );
        const response = await registerDevice({
          installationId: stored,
          pushToken: token.value,
          platform,
          audience,
          deviceName,
        });
        console.log('[push] register POST ok, installationId =', response.installationId);
        await storeInstallationId(response.installationId);
        resolveResult(response.installationId);
      } catch (err) {
        console.error('[push] register POST failed:', err);
        rejectResult(err);
      }
    },
  );

  const errorHandle = await PushNotifications.addListener(
    'registrationError',
    async (err) => {
      console.error('[push] registrationError event fired:', err);
      rejectResult(new Error(err.error ?? 'Push registration failed'));
    },
  );

  // Hard timeout so a missing 'registration' event can't lock the UI.
  // PushNotifications.register() on iOS resolves quickly (it just kicks
  // off registerForRemoteNotifications); the token arrives asynchronously
  // through the event. If APNs is unreachable, the entitlement doesn't
  // match the build's APNs environment (sandbox vs production), or the
  // cached token state is wedged, the event never fires and the await
  // below would hang forever. That hang bricked the Settings toggle in
  // build 19+ because the mount-time auto-recover kept notificationsBusy
  // stuck at true. 15 s is well past any realistic round-trip; if we hit
  // it, something is wrong and we want a visible error rather than a
  // frozen UI.
  const REGISTRATION_TIMEOUT_MS = 15_000;
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(
      () =>
        reject(
          new Error('Timed out waiting for push token. APNs may be unreachable.'),
        ),
      REGISTRATION_TIMEOUT_MS,
    );
  });

  try {
    console.log('[push] calling PushNotifications.register()');
    await PushNotifications.register();
    console.log('[push] register() returned, awaiting token event');
    const installationId = await Promise.race([resultPromise, timeoutPromise]);
    return installationId;
  } finally {
    await tokenHandle.remove().catch(() => undefined);
    await errorHandle.remove().catch(() => undefined);
  }
}

/**
 * Tear down on logout: tell the backend (and NH) to remove the
 * installation so further pushes don't land on a logged-out device.
 * Safe to call when not registered.
 */
export async function disablePush(): Promise<void> {
  if (!pushSupported()) return;

  const { unregisterDevice } = requireConfig();

  const installationId = await readStoredInstallationId();
  if (installationId) {
    try {
      await unregisterDevice(installationId);
    } catch {
      // Backend rejection (e.g. session already expired) shouldn't block
      // the logout flow; the NH installation will eventually be reaped
      // when APNs/FCM stops accepting the token.
    }
    await clearInstallationId();
  }
}

/**
 * Wire the once-per-process listeners for foreground / tap handling.
 * Foreground delivery shows an in-app banner instead of an OS one;
 * tap-through routing belongs in the app shell, not here, so we just
 * emit a CustomEvent the shell can subscribe to.
 *
 * Exported so the app shell can install the listeners at startup —
 * `enablePush()` also calls it, but if we relied on enablePush alone
 * a user who launches via a push tap (cold start) would lose the tap
 * event because the unlock / login screen runs before enablePush does.
 */
export function wirePushEventListeners(): void {
  if (listenersWired) return;
  listenersWired = true;

  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    window.dispatchEvent(
      new CustomEvent('push:received', { detail: notification }),
    );
  }).catch(() => undefined);

  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    window.dispatchEvent(new CustomEvent('push:tapped', { detail: action }));
  }).catch(() => undefined);
}
